import { cloneElement, createElement } from 'react';

type ComponentMap = Record<string, React.ReactElement | undefined>;

type RichTextProps = {
	/** String with pseudo-tags like <link>, <strong>, <em>. */
	message: string;
	/**
	 * Map of pseudo-tag names to React elements. Children and a key are injected
	 * automatically. Standard HTML tags (strong, em, p, span, small, div, br…)
	 * are always recognised and don't need to be listed here.
	 *
	 * @example
	 * components={{ link: <a href="https://example.com" rel="noopener noreferrer" target="_blank" /> }}
	 */
	components?: ComponentMap;
};

/** HTML tag names that may appear in message strings and are rendered as-is. */
const HTML_TAGS = new Set([
	'br',
	'div',
	'em',
	'hr',
	'li',
	'ol',
	'p',
	'small',
	'span',
	'strong',
	'ul'
]);

const VOID_TAGS = new Set(['br', 'hr']);

function decode(text: string): string {
	return text
		.replace(/&nbsp;/g, '\u00A0')
		.replace(/&amp;/g, '&')
		.replace(/&lt;/g, '<')
		.replace(/&gt;/g, '>')
		.replace(/&quot;/g, '"');
}

type Frame = { el: React.ReactElement | null; children: React.ReactNode[] };

function parse(message: string, components: ComponentMap): React.ReactNode[] {
	const TAG = /<(\/?)([a-zA-Z][a-zA-Z0-9-]*)(\s*\/)?>/g;
	const stack: Frame[] = [{ el: null, children: [] }];
	const top = () => stack[stack.length - 1]!;
	let cursor = 0;
	let key = 0;

	for (const match of message.matchAll(TAG)) {
		const [full, isClosing, rawName, isSelfClosing] = match;
		if (!rawName) continue;

		// Flush text before this tag
		if (match.index > cursor) {
			top().children.push(decode(message.slice(cursor, match.index)));
		}
		cursor = match.index + full.length;

		const tag = rawName.toLowerCase();
		const el = components[tag] ?? (HTML_TAGS.has(tag) ? createElement(tag) : null);

		if (isClosing) {
			// </tag> — pop frame and wrap its children in the mapped element
			if (stack.length > 1) {
				const { el: frameEl, children } = stack.pop()!;
				top().children.push(
					frameEl ? cloneElement(frameEl, { key: key++ }, ...children) : children
				);
			}
		} else if (isSelfClosing?.trim() === '/' || VOID_TAGS.has(tag)) {
			// <br /> or <br> — emit element with no children
			if (el) top().children.push(cloneElement(el, { key: key++ }));
		} else {
			// <tag> — push a new frame onto the stack
			stack.push({ el, children: [] });
		}
	}

	// Flush any trailing text
	if (cursor < message.length) {
		top().children.push(decode(message.slice(cursor)));
	}

	return stack[0]!.children;
}

/**
 * Renders a string containing pseudo-tags as React elements.
 * Never injects arbitrary HTML.
 *
 * @example
 * <RichText
 *   message="Es gelten die <link><strong>Gewährleistungsrechte</strong></link>."
 *   components={{ link: <a href="https://example.com" rel="noopener noreferrer" target="_blank" /> }}
 * />
 */
const RichText = ({ message, components = {} }: RichTextProps): React.ReactElement => (
	<>{parse(message, components)}</>
);

export { RichText };
export type { ComponentMap, RichTextProps };
